import * as THREE from "three";
import { VISUAL } from "../config/visualConfig";

// Static objects: observed motion must come from viewing position, not animation.
export class DepthTest {
  readonly group = new THREE.Group();
  private readonly depthObjects: THREE.Object3D[] = [];

  constructor() {
    const metal = (color: number) => new THREE.MeshStandardMaterial({ color, metalness: 0.55, roughness: 0.25 });
    const sphere = new THREE.Mesh(new THREE.SphereGeometry(1.15, 40, 24), metal(0x35eaff));
    sphere.position.set(0, 3.3, VISUAL.testDepth);
    const ring = new THREE.Mesh(new THREE.TorusGeometry(1.45, 0.22, 16, 64), metal(0xffffff));
    ring.position.set(0, 0, 0);
    const cube = new THREE.Mesh(new THREE.BoxGeometry(2, 2, 2), metal(VISUAL.back));
    cube.position.set(0, -3.3, -VISUAL.testDepth);
    cube.rotation.set(0.25, 0.4, 0);
    this.group.add(sphere, ring, cube);
    this.depthObjects.push(sphere, cube);

    for (const [y, z, text, color] of [
      [3.3, VISUAL.testDepth, "NEAR  +1.5", 0x35eaff],
      [0, 0, "FOCUS  0", 0xffffff],
      [-3.3, -VISUAL.testDepth, "FAR  -1.5", VISUAL.back],
    ] as const) {
      const label = this.label(text, color);
      label.position.set(0, y - 1.7, z);
      this.group.add(label);
      if (z !== 0) this.depthObjects.push(label);
    }

    // Zero-parallax vertical reference at the screen/focal plane.
    const reference = new THREE.Mesh(new THREE.BoxGeometry(0.04, 13.5, 0.04), metal(0x5f798f));
    reference.position.set(0, 0, 0);
    this.group.add(reference);
    for (const z of [-2, -1, 0, 1, 2]) {
      const frame = new THREE.LineSegments(
        new THREE.EdgesGeometry(new THREE.BoxGeometry(8, 14, 0.03)),
        new THREE.LineBasicMaterial({ color: z > 0 ? 0x237783 : 0x713465, transparent: true, opacity: 0.55 }),
      );
      frame.position.z = z;
      this.group.add(frame);
      if (z !== 0) this.depthObjects.push(frame);
    }
    this.group.visible = false;
  }

  setFocusOnly(enabled: boolean): void {
    for (const object of this.depthObjects) object.visible = !enabled;
  }

  private label(text: string, color: number): THREE.Mesh {
    const canvas = document.createElement("canvas");
    canvas.width = 512; canvas.height = 80;
    const context = canvas.getContext("2d")!;
    context.fillStyle = `#${color.toString(16).padStart(6, "0")}`;
    context.textAlign = "center";
    context.font = "bold 42px monospace";
    context.fillText(text, 256, 56);
    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    return new THREE.Mesh(new THREE.PlaneGeometry(3.7, 0.58), new THREE.MeshBasicMaterial({ map: texture, transparent: true }));
  }
}
